/* eslint-disable no-prototype-builtins */
import { Inject, Injectable, Optional } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig, DataUserDataService, DisplayDataFactoryService } from '@ddt-web/data-service';
import { EngagementPortfolio } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { StoreService } from '@ddt-web/store-service';
import { combineLatest, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';
import { PasDataLoaderService } from '../pas-setup-data-workers/services/pas-data-loader.service';

@Injectable()
export abstract class EngagementResolverWorker extends AbstractWorker {
	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		protected dataLoader: PasDataLoaderService,
		protected displayDataService: DisplayDataFactoryService,
		protected storeService: StoreService,
		@Optional() protected dataUserDataService?: DataUserDataService // optional since it is only needed if the engagement has models (ABP engagements only)
	) {
		super();
	}

	public loadEngagementData(queryParams, engagementPortfolios, modelsConfig, associatedPortfolios): void {
		const engagementPortfolio = engagementPortfolios?.entities[queryParams.engagement];

		if (!engagementPortfolio?.hasOwnProperty('success') && engagementPortfolio?.hasOwnProperty('error')) {
			// eslint-disable-next-line
			throw new ErrorObject(engagementPortfolio, 'engagementPortfolios');
		} else if (engagementPortfolio?.children) {
			const doesAssociatedPortfolioHaveLatestChanges = this.areUpdatedEngagementsInAssociatedPortfolios(engagementPortfolio, associatedPortfolios, (modelsConfig?.entities ?? {}));

			if (!doesAssociatedPortfolioHaveLatestChanges) {
				let currentPortfolios: [string, EngagementPortfolio][] = engagementPortfolio.children.map(portfolio => {
					// if the portfolio is a model portfolio and the portkey is in the config then use portkey as the id else use uniqueID
					const portfolioId = this.getEngagementPortfolioId(portfolio, (modelsConfig?.entities ?? {}));
					return [portfolioId, this.mapEngagementPortfolioToAssociatedPortfolio(portfolioId, portfolio)];
				});

				const currentModelIds: string[] = currentPortfolios.map(([id]) => id).filter(id => PortfolioIdUtil.isModel(id));
				// populate associated portfolios
				if (currentModelIds.length > 0) {
					// if the engagement contains model data, we will get the latest model data from the data service
					this.getLatestModelData(currentPortfolios, currentModelIds).pipe(first()).subscribe((currentModels) => {
						currentPortfolios = currentPortfolios.filter(([id]) => !currentModelIds.includes(id));
						this.storeEngagementPortfolios(queryParams, currentPortfolios, currentModels);
					});
				} else {
					// otherwise, don't call the models API and just store the engagement data for the portfolios
					this.storeEngagementPortfolios(queryParams, currentPortfolios);
				}
			}
		}
	}

	public conditionalTriggerForDataLoader(engagementId: string): void {
		combineLatest([
			this.storeService.selectAll(this.storeConfig.engagementPortfolios),
			this.storeService.selectAll(this.storeConfig.modelsConfig),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios)
		]).pipe(
			first(
				([engagementPortfolios, modelsConfig, associatedPortfolios]: any) =>
					modelsConfig && !modelsConfig.isLoading && engagementPortfolios && !engagementPortfolios.isLoading && engagementPortfolios.entities &&
					!!this.areUpdatedEngagementsInAssociatedPortfolios(engagementPortfolios.entities[engagementId], associatedPortfolios, modelsConfig.entities)
			)
		).subscribe(([engagementPortfolios, modelsConfig, associatedPortfolios]) => {
			this.setDataLoader(engagementId, engagementPortfolios, modelsConfig, associatedPortfolios);
		});
	}

	protected setDataLoader(engagementId, engagementPortfolios, modelsConfig, associatedPortfolios): void {
		const engagementPortfolio = engagementPortfolios.entities[engagementId];
		const ids = this.dataLoader.populateStoreForEngagement(engagementPortfolio, associatedPortfolios, modelsConfig?.entities ?? {});
		this.displayDataService.setDefaultIds(ids);
	}

	// Check if the associatedPortfolios contain already the engagement portfolios (they're readonly so no need ever to reload)
	protected areUpdatedEngagementsInAssociatedPortfolios(engagementPortfolio, associatedPortfolios, configEntities): boolean {
		return engagementPortfolio.children.every(engagementPortfolio => {
			// if the portfolio is a model portfolio and the portkey is in the config then use portkey as the id else use uniqueID
			const id = this.getEngagementPortfolioId(engagementPortfolio, configEntities);
			return associatedPortfolios.entities[id];
		});
	}

	protected getEngagementPortfolioId(engagementPortfolio, modelsConfigEntities): string {
		return modelsConfigEntities?.hasOwnProperty(engagementPortfolio?.portKey)
			? engagementPortfolio.portKey
			: engagementPortfolio.uniqueId;
	}

	protected mapEngagementPortfolioToAssociatedPortfolio(id: string, engagementPortfolio, fallbackPortfolioData?: EngagementPortfolio): EngagementPortfolio {
		return {
			aladdinPortfolioCode: engagementPortfolio?.aladdinPortfolioCode, // mainly needed for common period dialog mapping
			id: id ?? fallbackPortfolioData?.id,
			uniqueId: id ?? fallbackPortfolioData?.id,
			description: engagementPortfolio.description ?? fallbackPortfolioData?.description,
			dateLastModified: engagementPortfolio.dateLastModified ?? fallbackPortfolioData?.dateLastModified,
			outputDesignation: engagementPortfolio.outputDesignation ?? fallbackPortfolioData?.outputDesignation,
			parentId: engagementPortfolio.parentId ?? fallbackPortfolioData?.parentId,
			allowedPermsForCurrentUser: engagementPortfolio.allowedPermsForCurrentUser ?? fallbackPortfolioData?.allowedPermsForCurrentUser,
			type: engagementPortfolio.type ?? fallbackPortfolioData?.portfolioType,
			portKey: engagementPortfolio.portKey ?? fallbackPortfolioData?.portKey,
			positions: engagementPortfolio.children ?? fallbackPortfolioData?.positions,
			metadata: engagementPortfolio?.allData?.metadata
		} as EngagementPortfolio;
	}

	private storeEngagementPortfolios(queryParams, engagementPortfolios: [string, EngagementPortfolio][], engagementModels: EngagementPortfolio[] = []): void {
		this.storeService.addAll(this.storeConfig.associatedPortfolios, [...engagementPortfolios.map(([, portfolio]) => portfolio), ...engagementModels]);
		this.conditionalTriggerForDataLoader(queryParams.engagement);
	}

	private getLatestModelData(engagementPortfolios: [string, EngagementPortfolio][], modelIds: string[]): Observable<EngagementPortfolio[]> {
		return this.dataUserDataService.getPortfoliosById(modelIds).pipe(
			map(({ portfolios }) => {
				return portfolios.map(model => {
					const modelId: string = model['id'];
					const modelEngagementData: EngagementPortfolio = engagementPortfolios.find(([id]) => id === modelId)[1];
					return this.mapEngagementPortfolioToAssociatedPortfolio(modelId, model, modelEngagementData);
				});
			})
		);
	}
}
